import React, { useEffect, useRef } from "react";

// Icons for CTA section
const ShieldCheckIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const HeartSolidIcon = (props) => (
  <svg {...props} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
);

const ArrowRightIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

const ClockIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const HeartIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const categories = [
  { 
    title: "Adoption", 
    image: "/assets/cat1.png",
    gradient: "from-amber-500 to-orange-500",
    lightBg: "bg-amber-50",
    pattern: "bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-amber-100/40 via-amber-50/20 to-transparent",
    description: "Give a child a loving home"
  },
  { 
    title: "Animals & Pets", 
    image: "/assets/animals-pets-teal-bg.png",
    gradient: "from-teal-500 to-emerald-500",
    lightBg: "bg-teal-50",
    pattern: "bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-teal-100/40 via-teal-50/20 to-transparent",
    description: "Rescue, care, and protect"
  },
  { 
    title: "Business", 
    image: "/assets/business-teal-bg.png",
    gradient: "from-blue-500 to-indigo-500",
    lightBg: "bg-blue-50",
    pattern: "bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-blue-100/40 via-blue-50/20 to-transparent",
    description: "Turn ideas into reality"
  },
  { 
    title: "Competitive", 
    image: "/assets/competitive-teal-bg.png",
    gradient: "from-violet-500 to-purple-500",
    lightBg: "bg-violet-50",
    pattern: "bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-violet-100/40 via-violet-50/20 to-transparent",
    description: "Support athletes and teams"
  },
  { 
    title: "Creative", 
    image: "/assets/creative-teal-bg.png",
    gradient: "from-sky-500 to-cyan-500",
    lightBg: "bg-sky-50",
    pattern: "bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-sky-100/40 via-sky-50/20 to-transparent",
    description: "Bring art and ideas to life"
  },
  { 
    title: "Current Events", 
    image: "/assets/event-teal-bg.png",
    gradient: "from-rose-500 to-pink-500",
    lightBg: "bg-rose-50",
    pattern: "bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-rose-100/40 via-rose-50/20 to-transparent",
    description: "Respond to what matters now"
  }
];

export default function Category() {
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-card-reveal');
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
    );

    cardRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative bg-white py-0 overflow-hidden">
      
      {/* Premium animated background - simplified but kept premium feel */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-50/80 via-white to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-gray-50/80 via-white to-transparent"></div>
        
        {/* Animated gradient orbs - kept for premium feel */}
        <div className="absolute top-20 -left-20 w-[500px] h-[500px] bg-gradient-to-r from-amber-200/20 to-orange-200/20 rounded-full blur-3xl animate-orb-slow"></div>
        <div className="absolute top-40 -right-20 w-[500px] h-[500px] bg-gradient-to-r from-teal-200/20 to-emerald-200/20 rounded-full blur-3xl animate-orb"></div>
        <div className="absolute -bottom-20 left-1/3 w-[600px] h-[600px] bg-gradient-to-r from-blue-200/20 to-indigo-200/20 rounded-full blur-3xl animate-orb-slow"></div>
        <div className="absolute bottom-40 right-1/4 w-[400px] h-[400px] bg-gradient-to-r from-violet-200/20 to-purple-200/20 rounded-full blur-3xl animate-orb-delay"></div>
      </div>

      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 30 L30 30 Z' stroke='%2314b8a6' stroke-width='0.5'/%3E%3C/svg%3E")`,
        backgroundSize: '30px 30px'
      }}></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-0">
        
        {/* Section Header - ORIGINAL SIZES, REDUCED BOTTOM MARGIN */}
        <div className="text-center max-w-3xl mx-auto mb-8 md:mb-12">
          
          {/* Trust badge - SLIGHTLY SMALLER ON MOBILE */}
          <div className="inline-flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 bg-white/70 backdrop-blur-xl rounded-full border border-white/30 shadow-lg shadow-gray-200/20 mb-4 md:mb-6">
            <span className="relative flex h-1.5 w-1.5 md:h-2 md:w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-60"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 md:h-2 md:w-2 bg-teal-500"></span>
            </span>
            <span className="text-xs md:text-sm font-medium bg-gradient-to-r from-gray-700 to-gray-600 bg-clip-text text-transparent">
              Discover causes
            </span>
          </div>
          
          {/* Headline - SLIGHTLY SMALLER ON MOBILE */}
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-4 md:mb-6">
            Find your{' '}
            <span className="relative inline-block">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">
                purpose
              </span>
              <span className="absolute -bottom-2 md:-bottom-3 left-0 w-full h-1.5 md:h-2 bg-gradient-to-r from-teal-200/50 to-emerald-200/50 blur-sm"></span>
              <span className="absolute -bottom-2 md:-bottom-3 left-0 w-full h-0.5 md:h-1 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full"></span>
            </span>
          </h2>
          
          {/* Subheadline - SLIGHTLY SMALLER ON MOBILE */}
          <p className="text-base md:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed px-4">
            Every cause tells a story. Every story needs a champion.
          </p>
        </div>

        {/* Categories Grid - 2 COLUMNS ON MOBILE, 3 ON DESKTOP */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6 lg:gap-8 max-w-6xl mx-auto">
          {categories.map((item, index) => (
            <div
              key={item.title}
              ref={(el) => (cardRefs.current[index] = el)}
              className="group relative opacity-0 translate-y-10 transition-all duration-1000 cursor-pointer"
              style={{ transitionDelay: `${index * 120}ms` }}
            >
              
              {/* Card container - with layered depth */}
              <div className="relative">
                
                {/* Animated glow ring - REDUCED SIZE ON MOBILE */}
                <div className={`absolute -inset-1.5 md:-inset-3 bg-gradient-to-r ${item.gradient} rounded-2xl md:rounded-[36px] blur-md md:blur-2xl opacity-0 group-hover:opacity-30 group-hover:scale-110 transition-all duration-700`}></div>
                
                {/* Main card - FURTHER REDUCED PADDING FOR 2-COLUMN LAYOUT */}
                <div className="relative bg-white/90 backdrop-blur-sm rounded-xl md:rounded-3xl p-3 sm:p-4 md:p-7 lg:p-8 flex flex-col items-center border border-white/40 shadow-lg md:shadow-2xl shadow-gray-200/40 transition-all duration-500 group-hover:shadow-2xl group-hover:-translate-y-1 md:group-hover:-translate-y-3 group-hover:bg-white">
                  
                  {/* Animated pattern overlay */}
                  <div className={`absolute inset-0 ${item.pattern} opacity-0 group-hover:opacity-60 rounded-xl md:rounded-3xl transition-opacity duration-700`}></div>
                  
                  {/* Decorative top accent bar - REDUCED SIZE ON MOBILE */}
                  <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0.5 md:h-1 bg-gradient-to-r ${item.gradient} rounded-t-full group-hover:w-8 md:group-hover:w-16 transition-all duration-500`}></div>
                  
                  {/* Image container - FURTHER REDUCED SIZE FOR 2-COLUMN LAYOUT */}
                  <div className="relative mb-2 md:mb-5 transform transition-all duration-700 group-hover:scale-105 md:group-hover:scale-110 group-hover:-translate-y-1 md:group-hover:-translate-y-2">
                    
                    {/* Image frame - FURTHER REDUCED SIZE */}
                    <div className="relative w-14 h-14 xs:w-16 xs:h-16 sm:w-20 sm:h-20 md:w-28 md:h-28 lg:w-32 lg:h-36 flex items-center justify-center p-1.5 md:p-3">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="relative w-full h-full object-contain drop-shadow-sm md:drop-shadow-xl transition-all duration-700 group-hover:drop-shadow-md md:group-hover:drop-shadow-2xl z-10"
                      />
                    </div>
                  </div>

                  {/* Title - FURTHER REDUCED SIZE */}
                  <div className="relative mb-1 md:mb-3 text-center">
                    <h3 className={`text-xs xs:text-sm sm:text-base md:text-xl lg:text-2xl font-bold text-gray-800 mb-0.5 md:mb-1 transition-all duration-300 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r ${item.gradient} leading-tight`}>
                      {item.title}
                    </h3>
                    
                    {/* Animated underline - REDUCED SIZE */}
                    <div className="absolute -bottom-0.5 md:-bottom-1 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-teal-500 to-emerald-500 group-hover:w-6 md:group-hover:w-12 transition-all duration-500"></div>
                  </div>
                  
                  {/* Description - HIDDEN ON VERY SMALL SCREENS, SHOWN ON TABLET+ */}
                  <p className="hidden sm:block text-xs sm:text-sm text-gray-500 text-center leading-relaxed mb-2 md:mb-5 group-hover:text-gray-600 transition-colors duration-300 line-clamp-2 md:line-clamp-none">
                    {item.description}
                  </p>
                  
                  {/* Interactive elements - HIDDEN ON MOBILE */}
                  <div className="hidden sm:flex items-center justify-center gap-2 md:gap-3 w-full mt-1 md:mt-2">
                    
                    {/* Decorative dots */}
                    <div className="flex items-center gap-0.5 md:gap-1">
                      <span className={`w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-gradient-to-r ${item.gradient} opacity-30 group-hover:opacity-60 transition-opacity`}></span>
                      <span className={`w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-gradient-to-r ${item.gradient} opacity-30 group-hover:opacity-60 transition-opacity delay-100`}></span>
                      <span className={`w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-gradient-to-r ${item.gradient} opacity-30 group-hover:opacity-60 transition-opacity delay-200`}></span>
                    </div>
                    
                    {/* Decorative dots */}
                    <div className="flex items-center gap-0.5 md:gap-1">
                      <span className={`w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-gradient-to-r ${item.gradient} opacity-30 group-hover:opacity-60 transition-opacity delay-200`}></span>
                      <span className={`w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-gradient-to-r ${item.gradient} opacity-30 group-hover:opacity-60 transition-opacity delay-100`}></span>
                      <span className={`w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-gradient-to-r ${item.gradient} opacity-30 group-hover:opacity-60 transition-opacity`}></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Premium Call to Action - REDUCED TOP MARGIN */}
        <div className="text-center mt-8 md:mt-16">
          <div className="relative inline-flex flex-col items-center gap-4 md:gap-6">
            
            {/* Glow effect */}
            <div className="absolute -inset-3 md:-inset-4 bg-gradient-to-r from-teal-500/20 to-emerald-500/20 rounded-full blur-xl md:blur-2xl opacity-70"></div>
            
            {/* Emotional footer text - ORIGINAL SIZE */}
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-6 md:w-8 h-px bg-gradient-to-r from-transparent via-teal-300 to-transparent"></div>
              <p className="text-xs md:text-sm text-gray-400 italic">
                Your kindness creates ripples
              </p>
              <div className="w-6 md:w-8 h-px bg-gradient-to-r from-transparent via-teal-300 to-transparent"></div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== NEW: CTA WITH DONATEPE (ADDED AT THE ENDING) ===== */}
      <section className="relative overflow-hidden mt-12 md:mt-16">
        {/* Background gradient with brand */}
        <div className="absolute inset-0 bg-gradient-to-r from-teal-600 to-emerald-600"></div>
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }}></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 text-center">
          {/* Brand icon */}
          <div className="flex justify-center mb-4 md:mb-6">
            <div className="w-12 h-12 md:w-16 md:h-16 bg-white/20 backdrop-blur-sm rounded-xl md:rounded-2xl flex items-center justify-center text-white border border-white/30 shadow-xl">
              <HeartSolidIcon className="w-6 h-6 md:w-8 md:h-8 text-white" />
            </div>
          </div>
          
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 md:mb-3 px-4">
            Ready to start your journey with DonatePe?
          </h2>
          <p className="text-sm md:text-base lg:text-lg text-teal-50 mb-6 md:mb-8 max-w-2xl mx-auto px-4">
            Join thousands of campaigners who've raised crores for what matters.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 mb-6 md:mb-8">
            <a
              href="/start-fundraiser"
              className="group px-6 md:px-8 py-3 md:py-4 bg-white hover:bg-gray-100 text-gray-900 rounded-lg md:rounded-xl text-sm md:text-base font-medium shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
            >
              <span className="flex items-center gap-2">
                Start your free fundraiser
                <ArrowRightIcon className="w-3 h-3 md:w-4 md:h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </a>
          </div>
          
          {/* Trust indicators */}
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6 text-xs md:text-sm text-teal-50 px-4">
            <span className="flex items-center gap-1"><ShieldCheckIcon className="w-3 h-3 md:w-4 md:h-4" /> 4% platform fee(On Self Crowdfunding )</span>
            <span className="w-1 h-1 bg-teal-300 rounded-full hidden sm:block"></span>
            <span className="flex items-center gap-1"><ClockIcon className="w-3 h-3 md:w-4 md:h-4" /> 24/7 fund access</span>
            <span className="w-1 h-1 bg-teal-300 rounded-full hidden sm:block"></span>
            <span className="flex items-center gap-1"><ClockIcon className="w-3 h-3 md:w-4 md:h-4" /> 24/7 free support</span>
            <span className="w-1 h-1 bg-teal-300 rounded-full hidden sm:block"></span>
            <span className="flex items-center gap-1"><HeartIcon className="w-3 h-3 md:w-4 md:h-4" /> 50,000+ happy campaigners</span>
          </div>
          
          <p className="text-xs text-teal-200 mt-4 md:mt-6">
            DonatePe is trusted by individuals, NGOs, and communities across India.
          </p>
        </div>
      </section>

      <style jsx>{`
        @keyframes cardReveal {
          0% {
            opacity: 0;
            transform: translateY(40px) scale(0.96);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .animate-card-reveal {
          animation: cardReveal 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        
        @keyframes orb {
          0%, 100% { 
            transform: translate(0, 0) scale(1); 
            opacity: 0.3;
          }
          25% { 
            transform: translate(30px, -20px) scale(1.1); 
            opacity: 0.4;
          }
          50% { 
            transform: translate(10px, 30px) scale(0.95); 
            opacity: 0.35;
          }
          75% { 
            transform: translate(-20px, 10px) scale(1.05); 
            opacity: 0.4;
          }
        }
        
        .animate-orb {
          animation: orb 24s ease-in-out infinite;
        }
        
        .animate-orb-slow {
          animation: orb 32s ease-in-out infinite reverse;
        }
        
        .animate-orb-delay {
          animation: orb 28s ease-in-out infinite 2s;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* Extra small screen breakpoint */
        @media (min-width: 400px) {
          .xs\:w-16 {
            width: 4rem;
          }
          .xs\:h-16 {
            height: 4rem;
          }
          .xs\:text-sm {
            font-size: 0.875rem;
          }
        }
      `}</style>
    </section>
  );
}